/**
 * AI 填表员埋点 helper
 *
 * 协议参考 `/Users/comodo/Documents/明阳智能-大屏/新接口文档2/ai_form_filler_tracking.md`
 *
 * 5 个页面统一通过 trackEnter / trackClick / trackDialogShow / trackDialogClick
 * / trackResult 上报,避免 sensors.track('ai_click_...', ...) 字面值散落代码各处.
 *
 *   trackEnter('formFiller')                 → ai_enter_web_ai_form_filler
 *   trackClick('formFiller', 'upload_area')  → ai_click_web_ai_form_filler
 *   trackDialogShow('formFiller', 'confirm_delete_template')
 *   trackDialogClick('formFiller', 'confirm_delete_template', 'confirm')
 *   trackResult('formFilling', 'export_doc', { costTime: 1234 })
 *
 * `currentPageName` / `actionName` / `popTitle` / `clickItem` 等字段自动注入,
 * 调用方只关心场景维度.
 */
import sensors from '../../../utils/sensors'

const PAGES = {
  /** AI 填表员主页 */
  formFiller: 'web_ai_form_filler',
  /** 上传文档半屏 */
  uploadDoc: 'web_ai_upload_doc',
  /** 模板设置半屏 */
  templateSetting: 'web_ai_template_setting',
  /** AI 填写交互页(左预览 + 右对话) */
  formFilling: 'web_ai_form_filling',
  /** 换图选图半屏 */
  imagePicker: 'web_ai_image_picker'
} as const

export type PageKey = keyof typeof PAGES

type ExtraParams = Record<string, string | number | boolean | undefined>

/** 通用 entry,无外部 from/element/sessionId 时直接 trackEnter(page) */
export function trackEnter(page: PageKey, extra?: ExtraParams): void {
  sensors.track(`ai_enter_${PAGES[page]}`, {
    currentPageName: PAGES[page],
    ...(extra || {})
  })
}

/** 通用点击 */
export function trackClick(
  page: PageKey,
  clickItem: string,
  extra?: ExtraParams
): void {
  sensors.track(`ai_click_${PAGES[page]}`, {
    currentPageName: PAGES[page],
    clickItem,
    ...(extra || {})
  })
}

/** 弹窗曝光 */
export function trackDialogShow(
  page: PageKey,
  popTitle: string,
  extra?: ExtraParams
): void {
  sensors.track(`ai_dialog_show_${PAGES[page]}`, {
    currentPageName: PAGES[page],
    popTitle,
    ...(extra || {})
  })
}

/** 弹窗内点击 */
export function trackDialogClick(
  page: PageKey,
  popTitle: string,
  clickItem: string,
  extra?: ExtraParams
): void {
  sensors.track(`ai_dialog_click_${PAGES[page]}`, {
    currentPageName: PAGES[page],
    popTitle,
    clickItem,
    ...(extra || {})
  })
}

/**
 * 异步结果(上传/识别/导出等),含 costTime / errorMsg
 *
 * xlsx spec(ai_form_filler_tracking.xlsx)要求 actionResult 必填:
 *   - success: 业务成功(接口正常返回 / window.open 已触发)
 *   - fail: 业务失败(走 catch / 前置校验 fail),配合 errorMsg
 */
export function trackResult(
  page: PageKey,
  actionName: string,
  extra: ExtraParams & {
    actionResult: 'success' | 'fail'
    costTime?: number
    errorMsg?: string
  }
): void {
  sensors.track(`ai_result_${PAGES[page]}`, {
    currentPageName: PAGES[page],
    actionName,
    ...extra
  })
}

/**
 * 把组件内部 UsageState 映射到积分埋点契约要求的 string2 取值。
 *
 * 增量埋点文档：积分使用状态走通用槽 string2，仅 4 个取值：
 *   - unused       未使用 / 满额（used <= 0）
 *   - in_use       已使用一般（< 90%）
 *   - low_balance  余额预警（已用 >= 90%，但还够查询）
 *   - insufficient 不足 1 次
 *
 * PointsStatusCard 内部 UsageState 是 unused/normal/warning/critical（视觉档位），
 * 映射规则一一对应。所有积分相关事件（enter/click/dialog）都走这个 helper，避免
 * 拼写散落。
 */
export type CreditUsageTrackingString =
  | 'unused'
  | 'in_use'
  | 'low_balance'
  | 'insufficient'

export function mapCreditUsageState(
  state: 'unused' | 'normal' | 'warning' | 'critical'
): CreditUsageTrackingString {
  switch (state) {
    case 'unused':
      return 'unused'
    case 'normal':
      return 'in_use'
    case 'warning':
      return 'low_balance'
    case 'critical':
      return 'insufficient'
    default:
      return 'unused'
  }
}

/**
 * 读取当前会话 sessionId (= aiClerk:conversationId).
 * AI 填写交互页所有事件 / 换图选图半屏都需要带 sessionId. mount → 第一次 send
 * 之间 conversationId 还没生成时返回空串(spec 妥协,避免延迟上报)。
 */
const CONV_LS_KEY = 'aiClerk:conversationId'
export function readSessionId(): string {
  try {
    return window.localStorage.getItem(CONV_LS_KEY) || ''
  } catch {
    return ''
  }
}
